'use client'
import { motion, useInView } from 'framer-motion'
import { useRef, useEffect, useState } from 'react'
import SectionTitle from "@/components/ui/SectionTitle";
import YachtRouteCard, { type YachtRouteProps } from "@/components/ui/YachtRouteCard";
import PostCardSkeleton from "@/components/ui/PostCardSkeleton";
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext";
import type { PageUrlMap } from "@/types/blog";
import { debugWarn, logError } from "@/lib/utils/debug";
import { UrlBuilder } from "@/lib/url-builder";

interface YachtRouteGridProps {
    title?: string;
    titleTag?: string;
    subTitle?: string | null;
    subTitleTag?: string | null;
    description?: string | null;
    descriptionTag?: string | null;
    /**
     * Yacht route'ları (server-side fetch için)
     */
    routes?: YachtRouteProps[];
    /**
     * Dil kodu (opsiyonel)
     */
    language?: string;
    languageCode?: string;
    languageId?: number;
    /**
     * Kategori ID (filtreleme için) - tekil kategori için
     */
    categoryId?: number | string;
    /**
     * Kategori ID'leri (filtreleme için) - çoklu kategori için
     */
    categoryIds?: number[] | string[];
    /**
     * Kaç route gösterilecek (default: 3, max: 20)
     */
    limit?: number;
    /**
     * Props'tan veri gelmezse client-side'da veri çekmeyi etkinleştir
     * GrapeJS preview için kullanışlıdır
     */
    enableClientFetch?: boolean;
    pageUrls?: PageUrlMap;
    defaultLanguage?: string;
    __pageContext?: {
        language?: string;
        languageCode?: string;
        languageId?: number;
    };
}

export default function YachtRouteGrid({ 
    title = "Yacht Routes", 
    titleTag = "h2",
    subTitle = "Explore our routes", 
    subTitleTag = "span",
    description = null,
    descriptionTag = null,
           routes: initialRoutes = [],
           language,
           languageCode,
           languageId,
           categoryId,
           categoryIds,
           limit = 3,
    enableClientFetch = true,
    pageUrls,
    defaultLanguage = 'en',
    __pageContext,
}: YachtRouteGridProps) {
    const ref = useRef(null);
    const isInViewFromHook = useInView(ref, { once: true, margin: "-50px" });
    
    const [routes, setRoutes] = useState<YachtRouteProps[]>(initialRoutes);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    
    // API'den veri geldiğinde isInView'ı true yap (animasyonların çalışması için)
    const [dataLoaded, setDataLoaded] = useState(false);
    
    // Language variables için state
    const [variables, setVariables] = useState<Record<string, string>>({});

    const resolvedLanguageCode =
        language ||
        languageCode ||
        __pageContext?.language ||
        __pageContext?.languageCode ||
        undefined;
    const urlBuilder = pageUrls && resolvedLanguageCode
        ? new UrlBuilder(pageUrls, resolvedLanguageCode, defaultLanguage)
        : null;

    const resolvedLanguageId =
        languageId ||
        __pageContext?.languageId ||
        undefined;

    // Props'tan veri geldiyse client-side fetch yapma
    // ANCAK: Kategori filtresi varsa her zaman fetch yap (kategori değiştiğinde güncel veri için)
    const hasInitialData = initialRoutes.length > 0;
    const hasCategoryFilter = (categoryIds && Array.isArray(categoryIds) && categoryIds.length > 0) || !!categoryId;
    
    // Kategori filtresi varsa her zaman fetch yap (kategori değiştiğinde güncel veri için)
    // Kategori filtresi yoksa ve initialRoutes varsa fetch yapma
    // enableClientFetch false ise hiç fetch yapma
    const shouldFetch = enableClientFetch && (!hasInitialData || hasCategoryFilter);
    
    // Eğer initialRoutes varsa ve fetch yapılmayacaksa, dataLoaded'ı true yap
    const isInView = isInViewFromHook || dataLoaded;
    
    // Helper function to get variable with fallback
    const getVariable = (key: string, fallback: string): string => {
        return variables[key] || fallback;
    };
    

    // initialRoutes'u ref ile sakla - sadece gerçekten değiştiğinde güncelle
    const prevInitialRoutesLengthRef = useRef<number>(initialRoutes.length);
    const prevInitialRoutesIdsRef = useRef<string>(
        initialRoutes.map(r => String(r.id || '')).sort().join(',')
    );

    // Language variables - Context'ten al (duplicate fetch'i önlemek için)
    const { variables: contextVariables } = useLanguageVariables();
    
    // Context'ten gelen variables'ı kullan, yoksa boş object
    useEffect(() => {
        if (contextVariables && Object.keys(contextVariables).length > 0) {
            setVariables(contextVariables);
        }
    }, [contextVariables]);

    // initialRoutes değiştiğinde state'i güncelle (sadece shouldFetch false ise)
    useEffect(() => {
        if (!shouldFetch) {
            // Sadece gerçekten değiştiyse güncelle (length veya id'ler değişmişse)
            const currentIds = initialRoutes.map(r => String(r.id || '')).sort().join(',');
            const hasChanged = 
                prevInitialRoutesLengthRef.current !== initialRoutes.length ||
                prevInitialRoutesIdsRef.current !== currentIds;
            
            if (hasChanged) {
                setLoading(false);
                setError(null);
                setRoutes(initialRoutes);
                setDataLoaded(true); // Statik veri geldiğinde de animasyonların çalışması için
                prevInitialRoutesLengthRef.current = initialRoutes.length;
                prevInitialRoutesIdsRef.current = currentIds;
            } else if (hasInitialData && !dataLoaded) {
                // İlk render'da initialRoutes varsa, dataLoaded'ı true yap
                setDataLoaded(true);
            }
        } else if (hasInitialData && !dataLoaded) {
            // shouldFetch true olsa bile, eğer initialRoutes varsa dataLoaded'ı true yap
            setDataLoaded(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldFetch, initialRoutes, hasInitialData, dataLoaded]);

    // Client-side fetch için useEffect
    useEffect(() => {
        if (!shouldFetch) {
            return;
        }

        const abortController = new AbortController();
        const fetchRoutes = async () => {
            try {
                setLoading(true);
                setError(null);

                // API parametrelerini oluştur
                const params = new URLSearchParams();
                
                // Type parametresi
                params.set('type', 'yacht_routes');
                
                // Dil parametresini belirle
                let finalLanguage: string | undefined = undefined;
                
                if (resolvedLanguageCode) {
                    finalLanguage = resolvedLanguageCode;
                } else if (resolvedLanguageId) {
                    params.set('language_id', String(resolvedLanguageId));
                } else if (typeof window !== 'undefined') {
                    // window.location'dan dil kodu çıkar
                    const pathParts = window.location.pathname.split('/').filter(Boolean);
                    const possibleLang = pathParts[0];
                    if (possibleLang && possibleLang.length === 2 && /^[a-z]{2}$/.test(possibleLang)) {
                        finalLanguage = possibleLang;
                    } else {
                        // Cookie'den dil al
                        const cookieLang = document.cookie
                            .split('; ')
                            .find(row => row.startsWith('language='))
                            ?.split('=')[1];
                        finalLanguage = cookieLang || undefined;
                    }
                }

                if (finalLanguage) {
                    params.set('language', finalLanguage);
                }

                // Kategori filtresi - categoryIds (array) varsa onu kullan, yoksa categoryId (tekil) kullan
                if (categoryIds && Array.isArray(categoryIds) && categoryIds.length > 0) {
                    categoryIds.forEach(catId => {
                        params.append('category_id[]', String(catId));
                    });
                } else if (categoryId) {
                    params.set('category_id', String(categoryId));
                }

                const effectiveLimit = Math.min(Math.max(limit || 3, 1), 20);
                params.set('limit', String(effectiveLimit));

                const apiUrl = `/api/products?${params.toString()}`;

                const response = await fetch(apiUrl, {
                    signal: abortController.signal,
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    const errorMessage = errorData.message || errorData.error || `API error: ${response.status}`;
                    throw new Error(errorMessage);
                }

                const data = await response.json();
                
                // API response formatı: { success: true, data: Product[] }
                const apiProducts: any[] = data?.data || [];
                
                // Transform products to YachtRouteProps format
                const transformedRoutes: YachtRouteProps[] = apiProducts.map((product) => {
                    // Gallery images
                    const gallery = product.gallery || [];
                    const images: string[] = [];
                    const alts: string[] = [];
                    
                    if (Array.isArray(gallery)) {
                        gallery.forEach((item: any) => {
                            if (item.type === 'image' && item.url) {
                                images.push(item.url);
                                alts.push(item.alt || product.name || 'Yacht Route');
                            }
                        });
                    }

                    // Get route data from fields/metas
                    const fields = product.fields || {};
                    const metas = product.metas || {};
                    
                    const routeName = metas.route_name || fields.route_name?.field_value || product.name;
                    const startLocation = metas.start_location || fields.start_location?.field_value || null;
                    const endLocation = metas.end_location || fields.end_location?.field_value || null;
                    const durationHours = metas.duration_hours 
                        ? parseFloat(metas.duration_hours) 
                        : (fields.duration_hours?.field_value ? parseFloat(fields.duration_hours.field_value) : undefined);
                    const durationDays = metas.duration_days 
                        ? parseInt(metas.duration_days) 
                        : (fields.duration_days?.field_value ? parseInt(fields.duration_days.field_value) : undefined);
                    const routeType = metas.route_type || fields.route_type?.field_value || null;
                    const routeHighlights = metas.route_highlights || fields.route_highlights?.field_value || null;

                    return {
                        id: product.id,
                        src: images.length > 0 ? images : ['/placeholder-route.jpg'],
                        alt: alts.length > 0 ? alts : [product.name || 'Yacht Route'],
                        imageCount: images.length,
                        videoCount: 0, // TODO: video count if available
                        title: product.name || '',
                        routeName,
                        startLocation,
                        endLocation,
                        durationHours,
                        durationDays,
                        routeType,
                        routeHighlights,
                        slug: product.slug,
                        url: urlBuilder
                            ? urlBuilder.buildProductDetailUrl(
                                product.type || 'yacht_routes',
                                product.slug,
                                product.category?.slug,
                              )
                            : undefined,
                        is_featured: product.is_featured || false,
                    };
                });

                // State'i güncelle
                setRoutes(transformedRoutes);
                setDataLoaded(true); // Veri yüklendiğinde animasyonların çalışması için
            } catch (err) {
                if (abortController.signal.aborted) {
                    return;
                }
                logError('Yacht routes fetch error:', err);
                const errorMessage = err instanceof Error ? err.message : 'Yacht routes could not be loaded';
                setError(errorMessage);
                setRoutes([]);
                setDataLoaded(true); // Hata durumunda da animasyonların çalışması için
            } finally {
                if (!abortController.signal.aborted) {
                    setLoading(false);
                }
            }
        };

        fetchRoutes();
        return () => abortController.abort();
    }, [shouldFetch, resolvedLanguageCode, resolvedLanguageId, limit, categoryId, categoryIds]);

    // shouldFetch true ise fetched routes kullan, değilse props'tan gelen veriyi kullan
    const allRoutes = shouldFetch ? routes : initialRoutes;
    const hasData = allRoutes.length > 0;


    if (loading) {
        return (
            <motion.section
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{
                    duration: 0.6,
                    ease: "easeOut"
                }}
                className="relative mx-auto mt-16 mb-20 w-full min-w-0 max-w-9/12 px-4 sm:mt-24 sm:mb-28 md:mt-32 md:mb-36 lg:mt-[160px] lg:mb-[200px]"
            >
                <SectionTitle 
                    title={title} 
                    titleTag={titleTag} 
                    subTitle={subTitle} 
                    subTitleTag={subTitleTag} 
                    description={description} 
                    descriptionTag={descriptionTag}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-16">
                    {Array.from({ length: limit || 3 }).map((_, index) => (
                        <PostCardSkeleton key={index} index={index} />
                    ))}
                </div>
            </motion.section>
        );
    }

    if (error) {
        return (
            <motion.section
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{
                    duration: 0.6,
                    ease: "easeOut"
                }}
                className="relative mx-auto mt-16 mb-20 w-full min-w-0 max-w-9/12 px-4 sm:mt-24 sm:mb-28 md:mt-32 md:mb-36 lg:mt-[160px] lg:mb-[200px]"
            >
                <SectionTitle 
                    title={title} 
                    titleTag={titleTag} 
                    subTitle={subTitle} 
                    subTitleTag={subTitleTag} 
                    description={description} 
                    descriptionTag={descriptionTag}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-16 text-center text-red-500">
                    {getVariable('yacht_routes_load_error', 'Yacht routes could not be loaded')}
                </div>
            </motion.section>
        );
    }

    return (
        <motion.section
            className="relative mx-auto mt-16 mb-20 w-full min-w-0 max-w-9/12 px-4 sm:mt-24 sm:mb-28 md:mt-32 md:mb-36 lg:mt-[160px] lg:mb-[200px]"
        >
            <SectionTitle 
                title={title} 
                titleTag={titleTag} 
                subTitle={subTitle} 
                subTitleTag={subTitleTag} 
                description={description} 
                descriptionTag={descriptionTag}
            />
            
            {!hasData && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-16 text-center text-alsero-light-gray">
                    {getVariable('yacht_routes_no_routes', 'No yacht routes found for this category yet.')}
                </div>
            )}

            {hasData && (
                <div ref={ref} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-16">
                    {allRoutes.map((route, index) => (
                        <YachtRouteCard 
                            key={route.id || index} 
                            route={route} 
                            index={index} 
                            isInView={isInView}
                            language={resolvedLanguageCode}
                            languageCode={resolvedLanguageCode}
                            languageId={resolvedLanguageId}
                            pageUrls={pageUrls}
                            defaultLanguage={defaultLanguage}
                        />
                    ))}
                </div>
            )}
        </motion.section>
    )
}

